/* Render each dossier section using a structured pattern. */

function Label({ children }) {
  return <div className="text-overline text-muted-foreground mb-3">{children}</div>;
}
function Section({ title, children, testid }) {
  return (
    <section className="border-t border-border pt-8 pb-2" data-testid={testid}>
      <h3 className="font-display text-2xl mb-4">{title}</h3>
      {children}
    </section>
  );
}

function KeyValueGrid({ obj }) {
  if (!obj || typeof obj !== "object") return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{k.replaceAll("_", " ")}</div>
          <div className="text-sm mt-1.5 leading-relaxed">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
        </div>
      ))}
    </div>
  );
}

function List({ items }) {
  if (!Array.isArray(items)) return null;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="border-t border-border pt-2 text-sm leading-relaxed">
          {typeof it === "string" ? <span>• {it}</span> : (
            <div className="space-y-1">
              {Object.entries(it).map(([k, v]) => (
                <div key={k} className="flex gap-2 flex-wrap">
                  <span className="text-overline text-muted-foreground">{k.replaceAll("_", " ")}:</span>
                  <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function MarketSection({ data }) {
  if (!data) return <Empty />;
  const ms = data.market_size || {};
  return (
    <div className="space-y-8">
      {data.executive_summary && <p className="text-base leading-relaxed">{data.executive_summary}</p>}
      <Section title="Market Size" testid="market-size">
        <KeyValueGrid obj={ms} />
      </Section>
      <Section title="Growth Trends" testid="market-trends"><List items={data.growth_trends} /></Section>
      <Section title="Industry Dynamics" testid="market-dynamics"><List items={data.industry_dynamics} /></Section>
      <Section title="Competitive Landscape" testid="market-competition"><List items={data.competitive_landscape} /></Section>
      {data.swot && (
        <Section title="SWOT Analysis" testid="market-swot">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {[["Strengths","strengths"],["Weaknesses","weaknesses"],["Opportunities","opportunities"],["Threats","threats"]].map(([t,k]) => (
              <div key={k} className="bg-background p-4">
                <div className="text-overline text-muted-foreground mb-2">{t}</div>
                <ul className="space-y-1 text-sm">{(data.swot[k] || []).map((x,i) => <li key={i}>• {x}</li>)}</ul>
              </div>
            ))}
          </div>
        </Section>
      )}
      {data.porter_five_forces && (
        <Section title="Porter's Five Forces" testid="market-porter">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-px bg-border border border-border">
            {Object.entries(data.porter_five_forces).map(([k, v]) => (
              <div key={k} className="bg-background p-4">
                <div className="text-overline text-muted-foreground capitalize">{k.replaceAll("_"," ")}</div>
                <div className="data-large text-3xl mt-1">{v?.score || "—"}<span className="text-sm text-muted-foreground">/5</span></div>
                <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{v?.commentary}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {data.investment_opportunity_assessment && (
        <Section title="Investment Opportunity Assessment" testid="market-opportunity">
          <p className="text-sm leading-relaxed whitespace-pre-line">{data.investment_opportunity_assessment}</p>
        </Section>
      )}
    </div>
  );
}

function RegulatorySection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.summary && <p className="text-base leading-relaxed">{data.summary}</p>}
      {data.regulatory_complexity_score != null && (
        <div className="border border-border p-6 flex items-center justify-between" data-testid="reg-complexity">
          <div>
            <div className="text-overline text-muted-foreground">Regulatory complexity</div>
            <div className="text-sm text-muted-foreground mt-1">Lower is easier</div>
          </div>
          <div className="data-large text-5xl">{data.regulatory_complexity_score}<span className="text-base text-muted-foreground">/100</span></div>
        </div>
      )}
      <Section title="Licensing Requirements"><List items={data.licensing_requirements} /></Section>
      <Section title="Market Entry Requirements"><List items={data.market_entry_requirements} /></Section>
      {data.foreign_ownership && <Section title="Foreign Ownership"><KeyValueGrid obj={data.foreign_ownership} /></Section>}
      {data.tax_overview && <Section title="Tax Overview"><KeyValueGrid obj={data.tax_overview} /></Section>}
      <Section title="Import / Export Requirements"><List items={data.import_export_requirements} /></Section>
      {data.labor_law_overview && <Section title="Labor Law Overview"><KeyValueGrid obj={data.labor_law_overview} /></Section>}
      <Section title="Key Authorities"><List items={data.key_authorities} /></Section>
    </div>
  );
}

function RiskSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      <div className="border border-border p-6 flex items-center justify-between" data-testid="risk-overall">
        <div>
          <div className="text-overline text-muted-foreground">Overall risk score</div>
          <div className="text-sm text-muted-foreground mt-1 max-w-md">{data.risk_summary}</div>
        </div>
        <div className="data-large text-5xl">{data.overall_risk_score ?? "—"}<span className="text-base text-muted-foreground">/100</span></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.risks || []).map((r, i) => (
          <div key={i} className="border border-border p-5" data-testid={`risk-${r.category?.toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg">{r.category}</div>
                <div className="text-xs text-muted-foreground">{r.level}</div>
              </div>
              <div className="data-large text-2xl">{r.score}<span className="text-xs text-muted-foreground">/100</span></div>
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.description}</p>
            {r.mitigations && (
              <div className="mt-3 text-overline text-muted-foreground">Mitigations</div>
            )}
            <ul className="mt-1 text-sm space-y-1">{(r.mitigations || []).map((m, j) => <li key={j}>• {m}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function EcosystemSection({ data, hideGraphFields }) {
  if (!data) return <Empty />;
  const buckets = [
    ["Regulators", "regulators"], ["Ministries", "ministries"], ["Banks", "banks"],
    ["DFIs", "dfis"], ["Investors", "investors"], ["Industry Associations", "industry_associations"],
    ["Accelerators & Incubators", "accelerators_incubators"], ["Strategic Partners", "strategic_partners"],
  ];
  return (
    <div className="space-y-8">
      {data.summary && <p className="text-base leading-relaxed">{data.summary}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
        {buckets.map(([label, key]) => (
          <div key={key} className="bg-background p-5">
            <div className="text-overline text-muted-foreground mb-3">{label}</div>
            <ul className="space-y-2 text-sm">
              {(data[key] || []).map((it, i) => (
                <li key={i} className="border-t border-border pt-2 first:border-t-0 first:pt-0">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-muted-foreground">{it.role || it.focus || it.type || it.rationale || ""}</div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function DealsSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.summary && <p className="text-base leading-relaxed">{data.summary}</p>}
      <div className="border border-border">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 text-overline text-muted-foreground">Target</th>
              <th className="text-left p-3 text-overline text-muted-foreground">Type</th>
              <th className="text-left p-3 text-overline text-muted-foreground">Country</th>
              <th className="text-right p-3 text-overline text-muted-foreground">Opp</th>
              <th className="text-right p-3 text-overline text-muted-foreground">Fit</th>
            </tr>
          </thead>
          <tbody>
            {(data.pipeline || []).map((d, i) => (
              <tr key={i} className="border-b border-border hover:bg-accent/40 transition-colors">
                <td className="p-3">
                  <div className="font-medium">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.description}</div>
                </td>
                <td className="p-3 text-sm">{d.type}</td>
                <td className="p-3 text-sm text-muted-foreground">{d.country}</td>
                <td className="p-3 text-right data-large">{d.opportunity_score}</td>
                <td className="p-3 text-right data-large">{d.strategic_fit_score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.top_recommendations && (
        <Section title="Top Recommendations"><List items={data.top_recommendations} /></Section>
      )}
    </div>
  );
}

function ExpansionSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.market_entry_strategy && <Section title="Market Entry Strategy"><KeyValueGrid obj={data.market_entry_strategy} /></Section>}
      {data.go_to_market && <Section title="Go-to-Market"><KeyValueGrid obj={data.go_to_market} /></Section>}
      {data.distribution_strategy && <Section title="Distribution Strategy"><KeyValueGrid obj={data.distribution_strategy} /></Section>}
      {data.partnership_strategy && <Section title="Partnership Strategy"><KeyValueGrid obj={data.partnership_strategy} /></Section>}
      {data.expansion_roadmap && <Section title="Expansion Roadmap"><List items={data.expansion_roadmap} /></Section>}
      {data.commercialization_strategy && (
        <Section title="Commercialization Strategy">
          <p className="text-sm leading-relaxed whitespace-pre-line">{data.commercialization_strategy}</p>
        </Section>
      )}
    </div>
  );
}

function ExecutionSection({ data }) {
  if (!data) return <Empty />;
  const plans = [
    ["30-Day Plan", "thirty_day_plan"],
    ["90-Day Plan", "ninety_day_plan"],
    ["180-Day Roadmap", "one_eighty_day_roadmap"],
  ];
  return (
    <div className="space-y-8">
      {plans.map(([label, key]) => (
        <Section title={label} key={key}>
          <List items={data[key]} />
        </Section>
      ))}
      {data.key_milestones && <Section title="Key Milestones"><List items={data.key_milestones} /></Section>}
      {data.resource_requirements && <Section title="Resource Requirements"><KeyValueGrid obj={data.resource_requirements} /></Section>}
      {data.execution_risks && <Section title="Execution Risks"><List items={data.execution_risks} /></Section>}
      {data.recommended_priorities && <Section title="Priorities"><List items={data.recommended_priorities} /></Section>}
    </div>
  );
}

function ThesisSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.thesis_summary && <p className="text-base leading-relaxed">{data.thesis_summary}</p>}
      {data.value_creation_levers && (
        <Section title="Value Creation Levers" testid="thesis-levers">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.value_creation_levers.map((lv, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="font-semibold text-sm">{lv.lever}</div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${lv.impact === "High" ? "bg-teal-soft text-teal" : lv.impact === "Medium" ? "bg-gold-soft text-gold" : "bg-secondary text-muted-foreground"}`}>{lv.impact}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{lv.rationale}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
      {data.macro_tailwinds && <Section title="Macro Tailwinds"><List items={data.macro_tailwinds} /></Section>}
      {data.structural_advantages && <Section title="Structural Advantages"><List items={data.structural_advantages} /></Section>}
      {data.key_assumptions && (
        <Section title="Key Assumptions" testid="thesis-assumptions">
          <List items={data.key_assumptions} />
        </Section>
      )}
      {data.thesis_disconfirmers && <Section title="Thesis Disconfirmers"><List items={data.thesis_disconfirmers} /></Section>}
      {data.expected_outcomes && (
        <Section title="Expected Outcomes">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[["Base case", "base_case"], ["Upside", "upside_case"], ["Downside", "downside_case"]].map(([label, k]) => (
              <div key={k} className="border border-border rounded-xl p-4">
                <div className="text-overline text-muted-foreground mb-2">{label}</div>
                <p className="text-sm leading-relaxed">{data.expected_outcomes[k] || "—"}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function EsgSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.summary && <p className="text-base leading-relaxed">{data.summary}</p>}
      {data.composite_score != null && (
        <div className="border border-border p-6 flex items-center justify-between" data-testid="esg-composite">
          <div>
            <div className="text-overline text-muted-foreground">ESG Composite Score</div>
            <div className="text-sm text-muted-foreground mt-1">Aligned with IFC Performance Standards</div>
          </div>
          <div className="data-large text-5xl">{data.composite_score}<span className="text-base text-muted-foreground">/100</span></div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
        {[["Environmental", "environmental"], ["Social", "social"], ["Governance", "governance"]].map(([label, key]) => {
          const p = data[key] || {};
          return (
            <div key={key} className="bg-background p-5" data-testid={`esg-${key}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-display text-lg">{label}</div>
                <div className="data-large text-2xl">{p.score ?? "—"}<span className="text-xs text-muted-foreground">/100</span></div>
              </div>
              {p.key_issues && (
                <>
                  <div className="text-overline text-muted-foreground mt-3 mb-1">Issues</div>
                  <ul className="text-sm space-y-1">{(p.key_issues || []).map((x, i) => <li key={i}>• {x}</li>)}</ul>
                </>
              )}
              {p.mitigations && (
                <>
                  <div className="text-overline text-muted-foreground mt-3 mb-1">Mitigations</div>
                  <ul className="text-sm space-y-1">{(p.mitigations || []).map((x, i) => <li key={i}>• {x}</li>)}</ul>
                </>
              )}
            </div>
          );
        })}
      </div>
      {data.sdg_alignment && <Section title="SDG Alignment"><List items={data.sdg_alignment} /></Section>}
      {data.impact_kpis && <Section title="Impact KPIs"><List items={data.impact_kpis} /></Section>}
      {data.esg_red_flags && (
        <Section title="ESG Red Flags">
          <ul className="space-y-2 text-sm">
            {data.esg_red_flags.map((r, i) => (
              <li key={i} className="border-l-2 border-amber-500 pl-3 text-sm">⚠ {r}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function ValuationSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.summary && <p className="text-base leading-relaxed">{data.summary}</p>}
      {data.valuation_range && (
        <div className="grid grid-cols-3 gap-px bg-border border border-border" data-testid="valuation-range">
          {[["Low", "low"], ["Base", "base"], ["High", "high"]].map(([label, k]) => (
            <div key={k} className="bg-background p-5 text-center">
              <div className="text-overline text-muted-foreground">{label}</div>
              <div className="data-large text-2xl mt-2">{data.valuation_range[k] || "—"}</div>
            </div>
          ))}
        </div>
      )}
      {data.dcf_inputs && <Section title="DCF Inputs"><KeyValueGrid obj={data.dcf_inputs} /></Section>}
      {data.trading_multiples && (
        <Section title="Trading Multiples (Listed Peers)">
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr><th className="text-left p-3 text-overline text-muted-foreground">Metric</th>
                    <th className="text-right p-3 text-overline text-muted-foreground">Low</th>
                    <th className="text-right p-3 text-overline text-muted-foreground">Median</th>
                    <th className="text-right p-3 text-overline text-muted-foreground">High</th>
                    <th className="text-left p-3 text-overline text-muted-foreground">Source</th></tr>
              </thead>
              <tbody>
                {data.trading_multiples.map((m, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3 font-medium">{m.metric}</td>
                    <td className="p-3 text-right font-mono">{m.low}</td>
                    <td className="p-3 text-right font-mono">{m.median}</td>
                    <td className="p-3 text-right font-mono">{m.high}</td>
                    <td className="p-3 text-xs text-muted-foreground">{m.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {data.comparable_transactions && <Section title="Comparable Transactions"><List items={data.comparable_transactions} /></Section>}
      {data.valuation_methodology && (
        <Section title="Methodology"><p className="text-sm leading-relaxed">{data.valuation_methodology}</p></Section>
      )}
    </div>
  );
}

function MonteCarloSection({ data }) {
  if (!data) return <Empty />;
  if (data.error) return <div className="text-sm text-muted-foreground">Monte Carlo simulation not available.</div>;
  const fmt = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));
  const probs = data.probabilities || {};
  const moic = data.moic || {};
  const stats = [
    { k: "moic", label: "MOIC (x)" },
    { k: "irr", label: "IRR" },
    { k: "npv_eur", label: "NPV" },
    { k: "exit_value_eur", label: "Exit value" },
  ];
  return (
    <div className="space-y-8">
      <div className="border border-border p-6 flex items-start justify-between flex-wrap gap-4" data-testid="mc-header">
        <div>
          <div className="text-overline text-muted-foreground">Monte Carlo Simulation</div>
          <div className="text-sm text-muted-foreground mt-1">{(data.n_simulations || 0).toLocaleString()} stochastic scenarios · agent-derived distributions</div>
        </div>
        <div className="text-right">
          <div className="text-overline text-muted-foreground">Median MOIC</div>
          <div className="data-large text-3xl">{fmt(moic.median)}x</div>
        </div>
      </div>
      <Section title="Distributional Statistics" testid="mc-stats">
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left p-3 text-overline text-muted-foreground">Metric</th>
                <th className="text-right p-3 text-overline text-muted-foreground">Mean</th>
                <th className="text-right p-3 text-overline text-muted-foreground">Median</th>
                <th className="text-right p-3 text-overline text-muted-foreground">P5</th>
                <th className="text-right p-3 text-overline text-muted-foreground">P95</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => {
                const v = data[s.k] || {};
                return (
                  <tr key={s.k} className="border-t border-border">
                    <td className="p-3 font-medium">{s.label}</td>
                    <td className="p-3 text-right font-mono">{fmt(v.mean)}</td>
                    <td className="p-3 text-right font-mono">{fmt(v.median)}</td>
                    <td className="p-3 text-right font-mono">{fmt(v.p5)}</td>
                    <td className="p-3 text-right font-mono">{fmt(v.p95)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Risk-Return Probabilities" testid="mc-probabilities">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Probability of loss", v: probs.loss, color: "text-rose-600" },
            { label: "≥ 2x", v: probs.above_2x, color: "text-teal" },
            { label: "≥ 3x", v: probs.above_3x, color: "text-teal" },
            { label: "≥ 5x", v: probs.above_5x, color: "text-teal" },
          ].map((b) => (
            <div key={b.label} className="border border-border p-4 text-center">
              <div className="text-overline text-muted-foreground">{b.label}</div>
              <div className={`data-large text-2xl mt-1 ${b.color}`}>{((b.v || 0) * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      </Section>
      {data.assumptions && <Section title="Assumptions"><KeyValueGrid obj={data.assumptions} /></Section>}
    </div>
  );
}

function BayesianSection({ data }) {
  if (!data) return <Empty />;
  const pb = data.prior_belief || {};
  return (
    <div className="space-y-8">
      {data.summary && <p className="text-base leading-relaxed">{data.summary}</p>}
      {pb.verdict_probability_pursue != null && (
        <div className="border border-border p-6 flex items-center justify-between" data-testid="bay-prior">
          <div>
            <div className="text-overline text-muted-foreground">Prior P(pursue)</div>
            <div className="text-sm text-muted-foreground mt-1 max-w-xl">{pb.rationale}</div>
          </div>
          <div className="data-large text-5xl">{(pb.verdict_probability_pursue * 100).toFixed(0)}<span className="text-base text-muted-foreground">%</span></div>
        </div>
      )}
      {data.evidence_signals && (
        <Section title="Evidence Signals & Likelihood Ratios" testid="bay-evidence">
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr><th className="text-left p-3 text-overline text-muted-foreground">Signal</th>
                    <th className="text-right p-3 text-overline text-muted-foreground">LR</th>
                    <th className="text-left p-3 text-overline text-muted-foreground">Impact</th>
                    <th className="text-left p-3 text-overline text-muted-foreground">Timing</th></tr>
              </thead>
              <tbody>
                {data.evidence_signals.map((s, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3">{s.signal}</td>
                    <td className="p-3 text-right font-mono">{s.likelihood_ratio}</td>
                    <td className="p-3 text-xs">{s.impact}</td>
                    <td className="p-3 text-xs text-muted-foreground">{s.expected_timing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {data.posterior_scenarios && (
        <Section title="Posterior Scenarios" testid="bay-posterior">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.posterior_scenarios.map((s, i) => (
              <div key={i} className="border border-border p-4">
                <div className="text-overline text-muted-foreground mb-2">{s.scenario}</div>
                <div className="data-large text-2xl">{(Number(s.posterior_probability_pursue) * 100).toFixed(0)}%</div>
                <p className="text-xs text-muted-foreground mt-2">{s.implied_action}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
      {data.monitoring_kpis && <Section title="Monitoring KPIs"><List items={data.monitoring_kpis} /></Section>}
    </div>
  );
}

function StructuringSection({ data }) {
  if (!data) return <Empty />;
  return (
    <div className="space-y-8">
      {data.recommended_structure && (
        <div className="border border-border p-6 flex items-start justify-between flex-wrap gap-4" data-testid="struct-recommended">
          <div className="flex-1">
            <div className="text-overline text-muted-foreground">Recommended structure</div>
            <div className="font-display text-2xl mt-1">{data.recommended_structure}</div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{data.rationale}</p>
          </div>
        </div>
      )}
      {data.capital_stack && (
        <Section title="Capital Stack" testid="struct-stack">
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr><th className="text-left p-3 text-overline text-muted-foreground">Tranche</th>
                    <th className="text-right p-3 text-overline text-muted-foreground">Share</th>
                    <th className="text-left p-3 text-overline text-muted-foreground">Amount</th>
                    <th className="text-left p-3 text-overline text-muted-foreground">Instrument</th>
                    <th className="text-left p-3 text-overline text-muted-foreground">Rationale</th></tr>
              </thead>
              <tbody>
                {data.capital_stack.map((t, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3 font-medium">{t.tranche}</td>
                    <td className="p-3 text-right font-mono">{t.share_pct}%</td>
                    <td className="p-3 font-mono text-xs">{t.amount}</td>
                    <td className="p-3 text-xs">{t.instrument}</td>
                    <td className="p-3 text-xs text-muted-foreground">{t.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {data.co_investors_proposed && <Section title="Co-Investors Proposed"><List items={data.co_investors_proposed} /></Section>}
      {data.governance_terms && <Section title="Governance Terms"><KeyValueGrid obj={data.governance_terms} /></Section>}
      {data.deployment_tranching && <Section title="Deployment Tranching"><List items={data.deployment_tranching} /></Section>}
      {data.exit_pathways && (
        <Section title="Exit Pathways">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.exit_pathways.map((p, i) => (
              <div key={i} className="border border-border p-4">
                <div className="font-semibold text-sm">{p.path}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.horizon_years} years · {p.likelihood}</div>
                <p className="text-sm leading-relaxed mt-2">{p.rationale}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">This section is still being prepared.</div>;
}

export default function DossierSections({ data, only, hideGraphFields }) {
  if (!data) return <Empty />;
  if (only === "thesis") return <ThesisSection data={data.thesis} />;
  if (only === "market") return <MarketSection data={data.market} />;
  if (only === "regulatory") return <RegulatorySection data={data.regulatory} />;
  if (only === "risk") return <RiskSection data={data.risk} />;
  if (only === "esg") return <EsgSection data={data.esg} />;
  if (only === "valuation") return <ValuationSection data={data.valuation} />;
  if (only === "monte_carlo") return <MonteCarloSection data={data.monte_carlo} />;
  if (only === "bayesian") return <BayesianSection data={data.bayesian} />;
  if (only === "ecosystem") return <EcosystemSection data={data.ecosystem} hideGraphFields={hideGraphFields} />;
  if (only === "deals") return <DealsSection data={data.deals} />;
  if (only === "structuring") return <StructuringSection data={data.structuring} />;
  if (only === "expansion") return <ExpansionSection data={data.expansion} />;
  if (only === "execution") return <ExecutionSection data={data.execution} />;
  return null;
}
